<template>
  <v-dialog
    v-model="isVisible"
    content-class="auth-error"
    max-width="420px"
    :hide-overlay="true"
    :persistent="true"
    :no-click-animation="true"
    :scrollable="true"
  >
    <v-card
      v-if="error"
      ref="draggableModal"
      max-width="420px"
      max-height="60%"
      @mousedown="dragStart"
      @mousemove="dragging"
    >
      <v-row>
        <v-col class="col-auto ml-5 my-3 pa-0">
          <v-card-title>
            <div class="darkblue--text subtitle-2 text-uppercase">
              <span>
                Error {{ code }}
              </span>
            </div>
          </v-card-title>
        </v-col>

        <v-spacer />

        <v-col class="flex-shrink-1 col-auto mt-4 mr-4">
          <v-btn
            :ripple="false"
            icon
            small
            @click.stop="close()"
          >
            <font-awesome-icon
              :icon="['fas', 'xmark']"
              class="brightblue--text"
            />
          </v-btn>
        </v-col>
      </v-row>

      <v-divider />

      <v-card-text class="pt-3 pb-6">
        <v-col>
          <v-row>
            <span class="font-weight-bold">
              {{ message }}
            </span>
          </v-row>

          <v-row v-if="deniedEmail">
            <p class="mb-0">
              Signed in as <strong>{{ deniedEmail }}</strong>.
              This account is authenticated but does not currently have
              permission for this workspace.
            </p>
          </v-row>

          <v-row v-if="showAccessDeniedGuidance">
            <p class="mb-2">
              Try these steps before contacting support:
            </p>
            <ul class="mb-2">
              <li>Sign out and sign in with the correct work account.</li>
              <li>Confirm your administrator has granted access to this account.</li>
              <li>Retry after access changes are applied.</li>
            </ul>
            <p class="mb-0">
              We securely log technical diagnostics for this error. If you
              contact support, include the details below so we can trace the
              exact failure quickly.
            </p>
          </v-row>

          <v-row>
            <p>
              {{ details }}
            </p>

            <p>
              {{ hint }}
            </p>
          </v-row>

          <v-row>
            <span>
              Please try again
              or
              <a
                class="font-weight-bold pl-1"
                href="mailto:tech@oao.freshdesk.com"
              >
                contact us
              </a>
              with the details below.
            </span>
          </v-row>

          <v-row class="mt-6">
            <code
              class="offwhite pa-3 copy-field"
            >
              {{ JSON.stringify(supportDetails, undefined, 2) }}
            </code>
          </v-row>
        </v-col>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import { createNamespacedHelpers } from 'vuex'

import draggable from 'src/utils/draggable'

const { mapState } = createNamespacedHelpers('auth')

export default {
  name: 'AuthError',
  data() {
    return {
      copied: false,
      dragger: {},
      showCopyMessage: false,
    }
  },
  computed: {
    ...mapState({
      didSignInFail: (state) => state.didSignInFail,
      userEmail: (state) => state.userEmail,
    }),
    isVisible: {
      get() {
        return this.didSignInFail
      },
      set(value) {
        this.$store.commit('auth/SIGN_IN_FAILED', value)
      },
    },
    // didSignInFail IS the error object - read properties directly from it
    // (normalError.error is set to boolean `true`, not a nested error object)
    // BUT http.catch() creates a nested structure with error: { code, message, ... }
    // So we need to handle both cases
    error() {
      return this.didSignInFail || false
    },
    // Check if this is a nested error structure from http.catch
    // http.catch creates: { error: { code, message }, details, hint, errorId }
    nestedError() {
      const err = this.didSignInFail?.error
      return typeof err === 'object' ? err : null
    },
    code() {
      // Check nested error first, then top-level
      const { code } = this.nestedError || this.didSignInFail || {}
      return code || 500
    },
    rawMessage() {
      const { message } = this.nestedError || this.didSignInFail || {}
      return typeof message === 'string' ? message : ''
    },
    isAccessDeniedError() {
      const { code, message } = this.nestedError || this.didSignInFail || {}
      const is403 = code === '403' || code === 403

      return is403 && message === 'Access Denied'
    },
    isDeactivatedError() {
      const normalizedCode = String(this.code || '').toLowerCase()
      const normalizedMessage = this.rawMessage.toLowerCase()

      return normalizedMessage.includes('deactivated')
        && (normalizedMessage.includes('permission-denied')
          || normalizedCode === 'auth/internal-error'
          || normalizedCode === '403')
    },
    details() {
      if (this.isDeactivatedError) {
        return 'Your account is signed in but currently inactive. Access remains blocked until an administrator reactivates your account.'
      }

      return this.didSignInFail && this.didSignInFail.details
    },
    metadata() {
      return this.nestedError?.metadata || this.didSignInFail?.metadata || null
    },
    deniedEmail() {
      const email = this.metadata?.email || this.userEmail

      if (!this.showAccessDeniedGuidance) {
        return null
      }

      return typeof email === 'string' && email.length > 0 ? email : null
    },
    showAccessDeniedGuidance() {
      return this.isAccessDeniedError || this.isDeactivatedError
    },
    errorId() {
      return this.didSignInFail && this.didSignInFail.errorId
    },
    hint() {
      if (this.isDeactivatedError) {
        return 'If you believe this is incorrect, contact support or your workspace administrator and include the details below.'
      }

      const baseHint = this.didSignInFail && this.didSignInFail.hint

      // If no hint provided, check if we're on a preview channel and add domain guidance
      if (!baseHint) {
        const currentDomain = window?.location?.hostname || ''
        const isPreviewChannel = currentDomain.includes('--') && currentDomain.endsWith('.web.app')

        if (isPreviewChannel && this.message === 'Error Unknown') {
          return `This may be a domain configuration issue. The preview domain (${currentDomain}) may need to be registered with our security provider. Please contact a developer.`
        }
      }

      return baseHint
    },
    layer() {
      // Check nested error first, then top-level
      const { layer } = this.nestedError || this.didSignInFail || {}
      return layer || 'app'
    },
    supportDetails() {
      const details = {
        errorId: this.errorId,
        code: this.code,
        layer: this.layer,
      }

      if (this.isDeactivatedError) {
        details.reason = 'account-deactivated'
      } else if (this.isAccessDeniedError) {
        details.reason = 'access-denied'
      }

      if (this.deniedEmail) {
        details.email = this.deniedEmail
      }

      return details
    },
    message() {
      if (this.isDeactivatedError) {
        return 'Account Deactivated'
      }

      if (String(this.code || '').toLowerCase() === 'auth/internal-error' && this.rawMessage.toLowerCase().includes('permission-denied')) {
        return 'Access Denied'
      }

      // Check nested error first, then top-level
      const { message } = this.nestedError || this.didSignInFail || {}
      return message || 'Authentication Error'
    },
  },
  created() {
    document.addEventListener('mouseup', this.finishDragging)
  },
  beforeDestroy() {
    document.removeEventListener('mouseup', this.finishDragging)
  },
  methods: {
    close() {
      this.isVisible = false
    },
    ...draggable,
  },
}
</script>

<style lang="scss" scoped>
@import "~src/styles/variables";

.v-card {
  position: fixed;
  left: calc(50% - 175px);
  top: 36px;
}

.copy-field {
  max-height: 150px;
  overflow: scroll;
}

ul {
  padding-left: 20px;
}
</style>
